import { strval, round, intval, floatval } from './values';
import { PhpArray, PhpFloat } from './ordered-array';

// PHP 8.2+ case conversion is ASCII-only, independent of locale.
export const strtoupper = (s: string): string => s.replace(/[a-z]/g, c => String.fromCharCode(c.charCodeAt(0) - 32));
export const strtolower = (s: string): string => s.replace(/[A-Z]/g, c => String.fromCharCode(c.charCodeAt(0) + 32));
export const ucfirst = (s: string): string => strtoupper(s.slice(0, 1)) + s.slice(1);
export function ucwords(s: string, separators = ' \t\r\n\f\v'): string {
    let boundary = true;
    return Array.from(s).map(c => { const result = boundary ? strtoupper(c) : c; boundary = separators.includes(c); return result; }).join('');
}
function charset(chars: string): Set<string> {
    const result = new Set<string>();
    for (let i = 0; i < chars.length; i++) {
        if (chars.slice(i + 1, i + 3) === '..' && chars[i + 3] && chars.charCodeAt(i) <= chars.charCodeAt(i + 3)) {
            for (let code = chars.charCodeAt(i); code <= chars.charCodeAt(i + 3); code++) result.add(String.fromCharCode(code));
            i += 3;
        } else result.add(chars[i]!);
    }
    return result;
}
const DEFAULT_TRIM = ' \t\n\r\0\v';
function trimSide(s: string, chars: string, left: boolean, right: boolean): string {
    const set = charset(chars); let start = 0, end = s.length;
    if (left) while (start < end && set.has(s[start]!)) start++;
    if (right) while (end > start && set.has(s[end - 1]!)) end--;
    return s.slice(start, end);
}
export const trim = (s: string, chars = DEFAULT_TRIM): string => trimSide(s, chars, true, true);
export const ltrim = (s: string, chars = DEFAULT_TRIM): string => trimSide(s, chars, true, false);
export const rtrim = (s: string, chars = DEFAULT_TRIM): string => trimSide(s, chars, false, true);
export function str_replace(search: string | readonly string[], replacement: string | readonly string[], subject: string | PhpArray): string | PhpArray {
    if (subject instanceof PhpArray) return subject.copy(value => str_replace(search, replacement, strval(value)));
    let result = subject;
    const terms = typeof search === 'string' ? [search] : search;
    for (const [i, term] of terms.entries()) if (term !== '') result = result.split(term).join(typeof replacement === 'string' ? replacement : replacement[i] ?? '');
    return result;
}
export const str_contains = (s: string, needle: string): boolean => s.includes(needle);
export const str_starts_with = (s: string, needle: string): boolean => s.startsWith(needle);
function bytes(s: string): number[] {
    // No TextEncoder/DOM dependency; the port's strings are valid Unicode.
    const out: number[] = [];
    for (const char of s) {
        const n = char.codePointAt(0)!;
        if (n < 128) out.push(n);
        else if (n < 2048) out.push(192 | n >> 6, 128 | n & 63);
        else if (n < 65536) out.push(224 | n >> 12, 128 | n >> 6 & 63, 128 | n & 63);
        else out.push(240 | n >> 18, 128 | n >> 12 & 63, 128 | n >> 6 & 63, 128 | n & 63);
    }
    return out;
}
export function strcmp(a: string, b: string): number {
    const x = bytes(a), y = bytes(b);
    for (let i = 0; i < Math.min(x.length, y.length); i++) if (x[i] !== y[i]) return x[i]! - y[i]!;
    return Math.sign(x.length - y.length);
}
export const strcasecmp = (a: string, b: string): number => strcmp(strtolower(a), strtolower(b));
/* Altered TypeScript natural comparator based on PHP 8.5 strnatcmp.c.
 * https://github.com/php/php-src/blob/PHP-8.5/ext/standard/strnatcmp.c
 * Original notice (retained):
 * Modified for PHP by Andrei Zmievski <andrei@ispi.net>
 * strnatcmp.c -- Perform 'natural order' comparisons of strings in C.
 * Copyright (C) 2000 by Martin Pool <mbp@humbug.org.au>
 * This software is provided 'as-is', without any express or implied warranty.
 * In no event will the authors be held liable for any damages arising from
 * the use of this software.
 * Permission is granted to anyone to use this software for any purpose,
 * including commercial applications, and to alter it and redistribute it
 * freely, subject to the following restrictions:
 * 1. The origin of this software must not be misrepresented; you must not
 * claim that you wrote the original software. If you use this software in a
 * product, an acknowledgment in the product documentation would be
 * appreciated but is not required.
 * 2. Altered source versions must be plainly marked as such, and must not be
 * misrepresented as being the original software.
 * 3. This notice may not be removed or altered from any source distribution.
 */
export function strnatcmp(a: string, b: string): number {
    if (a === b) return 0;
    const x = bytes(a), y = bytes(b), digit = (n: number | undefined): boolean => n !== undefined && n >= 48 && n <= 57;
    const space = (n: number | undefined): boolean => n !== undefined && (n === 32 || n >= 9 && n <= 13);
    if (!x.length || !y.length) return Math.sign(x.length - y.length);
    let i = 0, j = 0;
    // PHP ignores initial zero padding only when followed by another digit.
    while (x[i] === 48 && digit(x[i + 1])) i++;
    while (y[j] === 48 && digit(y[j + 1])) j++;
    while (true) {
        while (space(x[i])) i++;
        while (space(y[j])) j++;
        if (digit(x[i]) && digit(y[j])) {
            let ii = i, jj = j, bias = 0;
            const fractional = x[i] === 48 || y[j] === 48;
            while (digit(x[ii]) && digit(y[jj])) {
                const d = x[ii++]! - y[jj++]!;
                if (d && fractional) return Math.sign(d);
                if (d && !bias) bias = Math.sign(d);
            }
            if (digit(x[ii]) !== digit(y[jj])) return digit(x[ii]) ? 1 : -1;
            if (bias) return bias;
            i = ii; j = jj;
            if (i === x.length || j === y.length) return i === x.length ? j === y.length ? 0 : -1 : 1;
        }
        const xx = x[i] ?? 0, yy = y[j] ?? 0;
        if (xx !== yy) return xx < yy ? -1 : 1;
        i++; j++;
        if (i >= x.length || j >= y.length) return i >= x.length ? j >= y.length ? 0 : -1 : 1;
    }
}
export const strnatcasecmp = (a: string, b: string): number => strnatcmp(strtoupper(a), strtoupper(b));
export function implode(separator: string, pieces: PhpArray | readonly unknown[] | Record<string, unknown>): string {
    return [...(pieces instanceof PhpArray ? pieces.values() : Object.values(pieces))].map(strval).join(separator);
}
export function explode(separator: string, s: string, limit = 2147483647): PhpArray<number, string> {
    if (!separator) throw new RangeError('explode separator cannot be empty');
    const parts = s.split(separator); limit = Math.trunc(limit);
    const result = limit < 0 ? parts.slice(0, Math.max(0, parts.length + limit))
        : parts.length > Math.max(1, limit) ? [...parts.slice(0, Math.max(1, limit) - 1), parts.slice(Math.max(1, limit) - 1).join(separator)] : parts;
    return new PhpArray(result.map((value, key) => [key, value]));
}
/** PHP sprintf's fixed conversion rounds binary doubles to nearest-even,
 * whereas JS toFixed chooses away on exact ties and emits exponents above 1e21.
 */
function fixed(value: number, places: number): string {
    if (!Number.isFinite(value)) return value < 0 ? '-INF' : Number.isNaN(value) ? 'NaN' : 'INF';
    const view = new DataView(new ArrayBuffer(8)); view.setFloat64(0, Math.abs(value));
    const bits = view.getBigUint64(0), exponent = Number(bits >> 52n & 2047n), fraction = bits & ((1n << 52n) - 1n);
    let numerator = exponent ? fraction + (1n << 52n) : fraction, denominator = 1n;
    const power = (exponent || 1) - 1023 - 52;
    if (power >= 0) numerator <<= BigInt(power); else denominator <<= BigInt(-power);
    numerator *= 10n ** BigInt(places);
    let rounded = numerator / denominator;
    const remainder = numerator % denominator;
    if (remainder * 2n > denominator || remainder * 2n === denominator && rounded % 2n !== 0n) rounded++;
    const digits = rounded.toString().padStart(places + 1, '0');
    return (value < 0 ? '-' : '') + (places ? digits.slice(0, -places) + '.' + digits.slice(-places) : digits);
}
/** Formats observed in includes/functions/janus: %s, %d, %u, %02d,
 * %-42s, %8.1f, %.3f and %%; positional, sign, padding and precision work too.
 * Deliberately throws on conversions not in this supported subset.
 */
export function sprintf(format: string, ...args: unknown[]): string {
    let cursor = 0;
    return format.replace(/%%|%((\d+)\$)?([-+ 0]*)(\d+)?(?:\.(\d+))?([a-zA-Z])/g, (token, _position: string, position: string, flags: string, width: string, precision: string, type: string) => {
        if (token === '%%') return '%';
        const index = position ? Number(position) - 1 : cursor++;
        if (index >= args.length) throw new RangeError('sprintf missing argument');
        const value = args[index], places = precision === undefined ? 6 : Number(precision);
        let text: string;
        if (type === 's') {
            text = strval(value);
            if (precision !== undefined) text = text.slice(0, places);
        } else if (type === 'd' || type === 'u') {
            const n = BigInt(intval(value));
            text = String(type === 'u' ? BigInt.asUintN(64, n) : n);
        } else if (type === 'f' || type === 'F') {
            const n = Number(floatval(value));
            text = fixed(n, places);
        } else throw new RangeError(`Unsupported sprintf conversion %${type}`);
        if (type !== 's' && !text.startsWith('-') && (flags.includes('+') || flags.includes(' '))) text = (flags.includes('+') ? '+' : ' ') + text;
        const padding = Math.max(0, Number(width ?? 0) - bytes(text).length);
        if (flags.includes('-')) return text + ' '.repeat(padding);
        if (flags.includes('0') && type !== 's') return /^[+ -]/.test(text) ? text[0] + '0'.repeat(padding) + text.slice(1) : '0'.repeat(padding) + text;
        return ' '.repeat(padding) + text;
    });
}
export function number_format(value: number | PhpFloat, decimals = 0, decimal_separator = '.', thousands_separator = ','): string {
    const rounded = Number(round(value, decimals)), places = Math.max(0, Math.trunc(decimals));
    const n = Number(value);
    if (!Number.isFinite(n)) return Number.isNaN(n) ? 'nan' : 'inf';
    const [integer, fraction] = fixed(Math.abs(rounded), places).split('.');
    const grouped = integer!.replace(/\B(?=(\d{3})+(?!\d))/g, thousands_separator);
    return (rounded < 0 ? '-' : '') + grouped + (places ? decimal_separator + fraction : '');
}
